import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AdminPageHeader } from '../Common/AdminPagesHeader';
import { REGULATORY_ISSUE_COLUMNS, REGULATORY_ISSUE, ROLES, QUERY_KEYS } from '../../../constants';
import { RegulatoryAddEdit, Table } from '../../shared';
import { usePagination } from '../../../hooks';
import { addNewRegulatoryIssues, getAllRegulatoryIssues, popup } from '../../../utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const RegulatoryIssue = () => {
    const queryClient = useQueryClient();
    const [response, setResponse] = useState({ data: [], count: 0 });
    const pagination = usePagination(response.data.length, response.count);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const role = useSelector((s) => s.user.role);

    const { data, isFetching, isError, error, isFetched } = useQuery({
        queryKey: [QUERY_KEYS.REGULATORY_ISSUE, pagination.limit, pagination.offset],
        queryFn: async () =>
            await getAllRegulatoryIssues({
                limit: pagination.limit,
                offset: pagination.offset,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
    });
    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data && isFetched) {
            setResponse(data);
        }
    }, [data, isFetched]);

    const addRegulatoryIssueMutation = useMutation({
        mutationFn: async (body) => await addNewRegulatoryIssues({ body }),
        onSuccess: () => {
            setIsAddModalOpen(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REGULATORY_ISSUE],
                exact: false,
            });
            popup('success', REGULATORY_ISSUE.CREATED_MESSAGE);
        },
        onError: (err) => {
            popup('error', err.message);
        },
    });

    const handleAddClick = ({ broadcast, regulatoryIssue }) => {
        const body = {
            broadcast,
            title: regulatoryIssue.title,
            content: regulatoryIssue.content,
        };
        addRegulatoryIssueMutation.mutate(body);
    };

    return (
        <>
            <AdminPageHeader
                showAddButton={ROLES.SALES_AGENT !== role}
                heading={REGULATORY_ISSUE.PAGE_HEADING}
                buttonText={REGULATORY_ISSUE.ADD_BUTTON_TEXT}
                handleAddClick={() => setIsAddModalOpen(true)}
                backbutton
                showSearchBar={false}
            />

            <Table
                columns={
                    ROLES.SALES_AGENT !== role
                        ? REGULATORY_ISSUE_COLUMNS
                        : REGULATORY_ISSUE_COLUMNS.filter((column) => column.name !== 'Action')
                }
                data={response.data}
                pagination={pagination}
                pageName={REGULATORY_ISSUE.PAGE_HEADING}
                stickyColumns={['Regulatory Issue']}
                isLoading={isFetching}
            />

            {isAddModalOpen && (
                <RegulatoryAddEdit
                    isModalOpen={isAddModalOpen}
                    setIsModalOpen={setIsAddModalOpen}
                    handleEditClick={handleAddClick}
                    modalType='add'
                    isLoading={addRegulatoryIssueMutation.isPending}
                />
            )}
        </>
    );
};
